
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { BarChart, TrendingUp, DollarSign, Percent, Calendar, Users, ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const PerformanceMetricCard = ({ title, value, change, period, icon: Icon }) => {
  const isPositive = parseFloat(change) > 0;
  const changeClass = isPositive ? "text-green-500" : "text-red-500";
  
  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex justify-between items-start">
          <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
          <Icon className="h-4 w-4 text-muted-foreground" />
        </div>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        <div className="flex items-center mt-1 text-xs">
          <span className={changeClass}>
            {isPositive ? "↑" : "↓"} {change}%
          </span>
          <span className="text-muted-foreground ml-2">vs. {period}</span>
        </div>
      </CardContent>
    </Card>
  );
};

const Performance = () => {
  const [timeRange, setTimeRange] = useState("6m");
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Performance Analytics</h1>
        <BarChart className="h-8 w-8 text-primary" />
      </div>
      
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-lg font-semibold">Key Performance Indicators</h2>
          <p className="text-muted-foreground">Monitoring your lending performance</p>
        </div>
        <Select defaultValue={timeRange} onValueChange={setTimeRange}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Select time range" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="1m">Last Month</SelectItem>
            <SelectItem value="3m">Last 3 Months</SelectItem>
            <SelectItem value="6m">Last 6 Months</SelectItem>
            <SelectItem value="1y">Last Year</SelectItem>
            <SelectItem value="all">All Time</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <PerformanceMetricCard
          title="Loan Volume"
          value="$1.24M"
          change="12.8"
          period="last period"
          icon={DollarSign}
        />
        <PerformanceMetricCard
          title="Average Interest Rate"
          value="5.2%"
          change="-0.3"
          period="last period"
          icon={Percent}
        />
        <PerformanceMetricCard
          title="Repayment Rate"
          value="97.8%"
          change="1.2"
          period="last period"
          icon={TrendingUp}
        />
        <PerformanceMetricCard
          title="Active Borrowers"
          value="327"
          change="5.6"
          period="last period"
          icon={Users}
        />
      </div>

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="funding">Funding</TabsTrigger>
          <TabsTrigger value="returns">Returns</TabsTrigger>
          <TabsTrigger value="growth">Growth</TabsTrigger>
        </TabsList>
        
        <TabsContent value="overview" className="mt-6 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Performance Overview</CardTitle>
              <CardDescription>Historical loan performance metrics</CardDescription>
            </CardHeader>
            <CardContent className="h-80 w-full flex items-center justify-center">
              <div className="text-center">
                <BarChart className="h-16 w-16 text-muted-foreground mx-auto" />
                <p className="mt-4 text-muted-foreground">
                  Performance charts would appear here
                </p>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Loan Performance by Category</CardTitle>
                <CardDescription>Distribution across loan types</CardDescription>
              </CardHeader>
              <CardContent className="h-60 flex items-center justify-center">
                <div className="text-center">
                  <ArrowUpRight className="h-10 w-10 text-muted-foreground mx-auto" />
                  <p className="mt-4 text-muted-foreground">
                    Category performance chart would appear here
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Timeframe Analysis</CardTitle>
                <CardDescription>Performance across time periods</CardDescription>
              </CardHeader>
              <CardContent className="h-60 flex items-center justify-center">
                <div className="text-center">
                  <Calendar className="h-10 w-10 text-muted-foreground mx-auto" />
                  <p className="mt-4 text-muted-foreground">
                    Timeframe analysis chart would appear here
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        
        <TabsContent value="funding" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Funding Performance</CardTitle>
              <CardDescription>Analysis of your funding activities</CardDescription>
            </CardHeader>
            <CardContent className="h-80 flex items-center justify-center">
              <div className="text-center">
                <DollarSign className="h-16 w-16 text-muted-foreground mx-auto" />
                <p className="mt-4 text-muted-foreground">
                  Funding performance metrics and charts would appear here
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="returns" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Returns Analysis</CardTitle>
              <CardDescription>Breakdown of your investment returns</CardDescription>
            </CardHeader>
            <CardContent className="h-80 flex items-center justify-center">
              <div className="text-center">
                <Percent className="h-16 w-16 text-muted-foreground mx-auto" />
                <p className="mt-4 text-muted-foreground">
                  Returns analysis metrics and charts would appear here
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="growth" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Growth Metrics</CardTitle>
              <CardDescription>Analysis of your business growth</CardDescription>
            </CardHeader>
            <CardContent className="h-80 flex items-center justify-center">
              <div className="text-center">
                <TrendingUp className="h-16 w-16 text-muted-foreground mx-auto" />
                <p className="mt-4 text-muted-foreground">
                  Growth metrics and charts would appear here
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <div className="flex justify-end">
        <Button className="flex items-center gap-2">
          Export Report <ArrowUpRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

export default Performance;
